import { Box } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'
import { TFunction } from 'i18next'
import groupBy from 'lodash/groupBy'
import isNil from 'lodash/isNil'
import { Fragment } from 'react'
import { PriceDisplay } from '@omnea-app/fe-legacy/components/shared/ui/price/PriceDisplay'
import { CurrencyCode } from '@omnea-app/packages/universal/currency-utils/getCurrencyCodes'
import { formatPriceValue } from '@omnea-app/packages/universal/price/formatPriceValue'
import { Dictionary } from '@omnea-app/packages/universal/utility-types/utilityTypes'
import { Price } from '@omnea-app/requests/price'
import { getPriceValue } from '@omnea-app/requests/price/getPriceValue'
import { BaseColumnConfig } from '../types'
import { baseColumnConfig } from './baseColumnConfig'
import { ColumnHeaderWithTooltip } from './helpers/ColumnHeaderWithTooltip'
import type { GridValidRowModel } from '@mui/x-data-grid/models/gridRows'
import type { CoreDataCurrency } from '@omnea-app/fe-core-data/useOrganisationCurrencies'

export type PriceColumnConfig<T extends GridValidRowModel> = BaseColumnConfig<T, 'price'> & {
  columnType: 'price'
  defaultCurrency: CurrencyCode
  termLabel: string
  currenciesByCode?: Dictionary<CoreDataCurrency>
  headerTooltip?: string
  t: TFunction<'requests'>
}

/* Handles columns with value Price[] | null  */
export const getPriceColumn = <T extends GridValidRowModel>({
  style,
  sortable = false,
  headerTooltip,
  ...config
}: PriceColumnConfig<T>): GridColDef<T> => ({
  ...baseColumnConfig,
  ...style,
  ...config,
  ...(headerTooltip !== undefined && {
    renderHeader: () => <ColumnHeaderWithTooltip label={config.headerName} tooltip={headerTooltip} />,
  }),
  groupable: false,
  sortable, // Opt-in per column — disabled by default since multi-price cells (e.g. $20 one-off, $40/month) can't be meaningfully sorted
  type: 'string',
  valueGetter: (_, row) => {
    const prices = row[config.field] as Price[] | null | undefined

    return getPriceValue({
      currenciesByCode: config.currenciesByCode,
      defaultCurrency: config.defaultCurrency,
      termLabel: config.termLabel,
      prices,
      t: config.t,
    })
  },

  valueFormatter: (value) => {
    return formatPriceValue({ value, defaultCurrency: config.defaultCurrency })
  },
  renderCell: (params) => {
    const prices = params.row[config.field]
    if (isNil(prices)) return null

    const pricesGroupedByTerm = groupBy(prices, 'termIndex')
    const termEntries = Object.entries(pricesGroupedByTerm)
    const numberOfTerms = termEntries.length

    return (
      <>
        {termEntries.map(([termIndex, pricesInTerm]) => (
          <Box key={termIndex} display="flex" alignItems="center">
            {numberOfTerms > 1 && (
              <Box mr={2}>
                {config.termLabel} {Number(termIndex) + 1}:
              </Box>
            )}
            {pricesInTerm.map((price: Price, index: number) => (
              <Fragment key={index}>
                <PriceDisplay price={price} direction="column" />
                {index < pricesInTerm.length - 1 && (
                  <Box
                    mx={2}
                    sx={{
                      width: '1px',
                      alignSelf: 'stretch',
                      backgroundColor: 'border.subtle',
                    }}
                  />
                )}
              </Fragment>
            ))}
            {Number(termIndex) < numberOfTerms - 1 && (
              <Box
                mx={2}
                sx={{
                  width: '5px',
                  alignSelf: 'stretch',
                  backgroundColor: 'border.subtle',
                }}
              />
            )}
          </Box>
        ))}
      </>
    )
  },
})
